import React, { useState, useEffect, useRef } from 'react';
import { Col, Row } from 'reactstrap';
import { withStyles } from '@material-ui/core';
import * as Constants from '../Pages/Constants';
import DataServiceSQL from '../DataService/DataServiceSQL';
import { FormattedMessage, useIntl } from 'react-intl';
import { Icon } from 'semantic-ui-react';
import { Tooltip } from 'react-tooltip';
import AddStaff from '../Components/Staff/AddStaff.js';
import AddUser from '../Components/User/AddUser.js';
import AddStructurePatient from '../Components/Structure/AddStructurePatient.js';
import Utils from '../Components/Common/Utils.js';
import './StructureManagementMenu.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const getActiveFeatures = (features) => {
  if (features == null) return [];
  const bits = Number(features);
  return Utils.FEATURE_NAMES.filter((_, i) => (bits >> i) & 1);
};

const StructureManagementMenu = (props) => {
  const { classes } = props;
  const intl = useIntl();
  const isAdmin = props.accountInformation && props.accountInformation.role === "admin";
  const isSuperAdmin = isAdmin && props.accountInformation.role_extra === "admin_etc";

  const [allStructures, setAllStructures] = useState([]);
  const [filteredStructures, setFilteredStructures] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ structure_name: "", type: "", email: "", phone_number: "", address: "", hours_day_start: "09:00", hours_day_end: "18:00", hours_night_start: "18:00", hours_night_end: "09:00" });
  const [addError, setAddError] = useState("");
  const [addMessage, setAddMessage] = useState("");
  const [addSubmitted, setAddSubmitted] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState("");
  const [subscriptionFilter, setSubscriptionFilter] = useState(props.initialSubscriptionFilter || null);
  const [selectedStructure, setSelectedStructure] = useState(null);
  const [structureUsers, setStructureUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  const [addPersonStructure, setAddPersonStructure] = useState(null);
  const [addPersonRole, setAddPersonRole] = useState(null);
  const [addPatientStructure, setAddPatientStructure] = useState(null);
  const [allSubscriptions, setAllSubscriptions] = useState([]);
  const [assignSubStructure, setAssignSubStructure] = useState(null);
  const [assignSubSelected, setAssignSubSelected] = useState("");
  const [assignSubLoading, setAssignSubLoading] = useState(false);
  const [assignSubError, setAssignSubError] = useState("");
  // PSAD access modal: which PSAD structures a (non-PSAD) structure may use.
  const [psadAccessStructure, setPsadAccessStructure] = useState(null);
  const [allPsadStructures, setAllPsadStructures] = useState([]);
  const [psadAccessSelected, setPsadAccessSelected] = useState([]);
  const [psadAccessLoading, setPsadAccessLoading] = useState(false);
  const [psadAccessError, setPsadAccessError] = useState("");
  // Practitioner access modal: which practitioners a structure may list in the
  // concerned-speciality "Platform practitioners" dropdown.
  const [practAccessStructure, setPractAccessStructure] = useState(null);
  const [allPractitioners, setAllPractitioners] = useState([]);
  const [practAccessSelected, setPractAccessSelected] = useState([]);
  const [practAccessLoading, setPractAccessLoading] = useState(false);
  const [practAccessError, setPractAccessError] = useState("");
  // Per-user credential sending state in the users panel
  const [credSendingUserId, setCredSendingUserId] = useState(null);
  const [credSentUserId, setCredSentUserId] = useState(null);
  const [credErrorUserId, setCredErrorUserId] = useState(null);
  // Structure creation: optional account creation
  const [addCreateAccount, setAddCreateAccount] = useState(false);
  // Structure account modal: resend or create the login account tied to the structure email
  const [accountStructure, setAccountStructure] = useState(null);
  const [accountExistingUser, setAccountExistingUser] = useState(null);
  const [accountChecking, setAccountChecking] = useState(false);
  const [accountFirstName, setAccountFirstName] = useState("");
  const [accountLastName, setAccountLastName] = useState("");
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountError, setAccountError] = useState("");
  const [accountSent, setAccountSent] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [copiedSubId, setCopiedSubId] = useState(null);

  const allRef = useRef([]);

  useEffect(() => {
    loadStructures();
    if (isSuperAdmin) loadSubscriptions();
    // eslint-disable-next-line
  }, []);

  const loadStructures = async () => {
    setLoading(true);
    try {
      const response = await DataServiceSQL.getAllStructures();
      const data = response.data || [];
      allRef.current = data;
      setAllStructures(data);
      applyFilters(searchQuery, typeFilter, subscriptionFilter);
    } catch (e) {
      allRef.current = [];
      setAllStructures([]);
      setFilteredStructures([]);
    }
    setLoading(false);
  };

  const loadSubscriptions = async () => {
    try {
      const response = await DataServiceSQL.getAllSubscriptions();
      setAllSubscriptions(response.data || []);
    } catch (e) {
      setAllSubscriptions([]);
    }
  };

  const applyFilters = (query, type, subFilter = subscriptionFilter) => {
    let result = allRef.current;
    if (type) {
      result = result.filter(s => s.type === type);
    }
    if (subFilter) {
      result = result.filter(s => s.FK_subscription === subFilter);
    }
    if (query.trim().length > 0) {
      const q = query.toLowerCase();
      result = result.filter(s =>
        (s.structure_name && s.structure_name.toLowerCase().includes(q)) ||
        (s.type && s.type.toLowerCase().includes(q)) ||
        (s.email && s.email.toLowerCase().includes(q)) ||
        (s.address && s.address.toLowerCase().includes(q)) ||
        (s.id_structure && s.id_structure.toLowerCase().includes(q))
      );
    }
    setFilteredStructures(result);
    setCurrentPage(1);
  };

  const clearSubscriptionFilter = () => {
    setSubscriptionFilter(null);
    applyFilters(searchQuery, typeFilter, null);
  };

  const onTypeFilterChange = (type) => {
    setTypeFilter(type);
    applyFilters(searchQuery, type);
  };

  const onSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (value === "") {
      applyFilters("", typeFilter);
    }
  };

  const onSearchKeyDown = (e) => {
    if (e.key === 'Enter' && searchQuery.length >= 1) {
      applyFilters(searchQuery, typeFilter);
    }
  };

  const applySearch = () => {
    applyFilters(searchQuery, typeFilter);
  };

  const clearSearch = () => {
    setSearchQuery("");
    applyFilters("", typeFilter);
  };

  const totalPages = Math.max(1, Math.ceil(filteredStructures.length / pageSize));
  const paginatedStructures = filteredStructures.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const onPageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleSendUserCredentials = async (user) => {
    if (!selectedStructure || !user.id_user) return;
    setCredSendingUserId(user.id_user);
    setCredSentUserId(null);
    setCredErrorUserId(null);
    try {
      await DataServiceSQL.sendStructureCredentials(selectedStructure.id_structure, {
        id_user: user.id_user,
        language: intl.locale || 'fr-FR',
      });
      setCredSentUserId(user.id_user);
    } catch (e) {
      setCredErrorUserId(user.id_user);
    }
    setCredSendingUserId(null);
  };

  const openAddForm = () => {
    setAddForm({ structure_name: "", type: "", email: "", phone_number: "", address: "", hours_day_start: "09:00", hours_day_end: "18:00", hours_night_start: "18:00", hours_night_end: "09:00", FK_subscription: "", admin_first_name: "", admin_last_name: "" });
    setAddError("");
    setAddMessage("");
    setAddSubmitted(false);
    setAddLoading(false);
    setAddCreateAccount(false);
    setShowAddForm(true);
  };

  const handleAddFormInput = (e) => {
    const { name, value } = e.target;
    setAddForm(prev => ({ ...prev, [name]: value }));
  };

  const saveStructure = async () => {
    if (!addForm.structure_name.trim()) {
      setAddError(intl.formatMessage({ id: 'structure.error.name', defaultMessage: 'Structure name is required' }));
      return;
    }
    if (addCreateAccount && (!addForm.admin_first_name.trim() || !addForm.admin_last_name.trim() || !addForm.email.trim())) {
      setAddError(intl.formatMessage({ id: 'structure.error.account', defaultMessage: 'Prénom, nom et email sont requis pour créer un compte.' }));
      return;
    }
    setAddError("");
    setAddLoading(true);
    setAddMessage(intl.formatMessage({ id: 'structure.creating', defaultMessage: 'Creating structure...' }));
    let hours_schedule_val = undefined;
    const hasDaySlot = addForm.hours_day_start || addForm.hours_day_end;
    const hasNightSlot = addForm.hours_night_start || addForm.hours_night_end;
    if (hasDaySlot || hasNightSlot) {
      const hs = {};
      if (hasDaySlot) hs.day = `${addForm.hours_day_start || "00:00"},${addForm.hours_day_end || "00:00"}`;
      if (hasNightSlot) hs.night = `${addForm.hours_night_start || "00:00"},${addForm.hours_night_end || "00:00"}`;
      hours_schedule_val = JSON.stringify(hs);
    }
    const payload = {
      structure_name: addForm.structure_name.trim(),
      type: addForm.type || undefined,
      email: addForm.email.trim() || undefined,
      phone_number: addForm.phone_number.trim() || undefined,
      address: addForm.address.trim() || undefined,
      hours_schedule: hours_schedule_val,
      FK_subscription: addForm.FK_subscription || undefined
    };
    try {
      const createRes = await DataServiceSQL.createStructure(payload);
      if (addCreateAccount) {
        const accountEmail = addForm.email.trim();
        const firstName    = addForm.admin_first_name.trim();
        const lastName     = addForm.admin_last_name.trim();
        if (accountEmail && firstName && lastName && createRes && createRes.data && createRes.data.id_structure) {
          try {
            await DataServiceSQL.sendStructureCredentials(createRes.data.id_structure, {
              first_name: firstName,
              last_name:  lastName,
              email:      accountEmail,
              language:   intl.locale || 'fr-FR',
            });
          } catch (credErr) {
            setAddMessage(intl.formatMessage({ id: 'structure.created.credError', defaultMessage: 'Structure créée mais envoi des identifiants échoué.' }));
            setAddSubmitted(true);
            setAddLoading(false);
            return;
          }
        }
      }
      setAddMessage(intl.formatMessage({ id: 'message.success', defaultMessage: 'Success!' }));
      setAddSubmitted(true);
      setAddLoading(false);
    } catch (e) {
      setAddMessage(intl.formatMessage({ id: 'message.error', defaultMessage: 'An error occurred' }));
      setAddLoading(false);
      setAddSubmitted(true);
    }
  };

  const closeAddForm = () => {
    setShowAddForm(false);
    loadStructures();
  };

  const openAssignSub = (s) => {
    setAssignSubStructure(s);
    setAssignSubSelected(s.FK_subscription || "");
    setAssignSubError("");
  };

  const saveAssignSubscription = async () => {
    if (!assignSubStructure) return;
    setAssignSubLoading(true);
    setAssignSubError("");
    try {
      await DataServiceSQL.assignSubscriptionToStructure(assignSubStructure.id_structure, assignSubSelected || null);
      setAssignSubStructure(null);
      loadStructures();
    } catch (e) {
      setAssignSubError(intl.formatMessage({ id: 'message.error', defaultMessage: 'An error occurred' }));
    }
    setAssignSubLoading(false);
  };

  const openPsadAccess = async (s) => {
    setPsadAccessStructure(s);
    setPsadAccessError("");
    setPsadAccessSelected([]);
    setAllPsadStructures([]);
    try {
      const [allRes, accessRes] = await Promise.all([
        DataServiceSQL.getStructuresByType('psad'),
        DataServiceSQL.getStructurePsadAccess(s.id_structure),
      ]);
      setAllPsadStructures(allRes.data || []);
      setPsadAccessSelected((accessRes.data && accessRes.data.assigned_psad_ids) || []);
    } catch (e) {
      setPsadAccessError(intl.formatMessage({ id: 'structure.psad.loadError', defaultMessage: 'Erreur de chargement des PSAD' }));
    }
  };

  const togglePsadAccess = (id) => {
    setPsadAccessSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const savePsadAccess = async () => {
    if (!psadAccessStructure) return;
    setPsadAccessLoading(true);
    setPsadAccessError("");
    try {
      await DataServiceSQL.setStructurePsadAccess(psadAccessStructure.id_structure, psadAccessSelected);
      setPsadAccessStructure(null);
    } catch (e) {
      setPsadAccessError(intl.formatMessage({ id: 'message.error', defaultMessage: 'An error occurred' }));
    }
    setPsadAccessLoading(false);
  };

  const openPractitionerAccess = async (s) => {
    setPractAccessStructure(s);
    setPractAccessError("");
    setPractAccessSelected([]);
    setAllPractitioners([]);
    try {
      const [allRes, accessRes] = await Promise.all([
        DataServiceSQL.listExistingPractitioners("", null, 1000),
        DataServiceSQL.getStructurePractitionerAccess(s.id_structure),
      ]);
      setAllPractitioners(allRes.data || []);
      setPractAccessSelected((accessRes.data && accessRes.data.assigned_practitioner_ids) || []);
    } catch (e) {
      setPractAccessError(intl.formatMessage({ id: 'structure.managePractitioners.loadError', defaultMessage: 'Erreur de chargement des praticiens' }));
    }
  };

  const togglePractitionerAccess = (id) => {
    setPractAccessSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const savePractitionerAccess = async () => {
    if (!practAccessStructure) return;
    setPractAccessLoading(true);
    setPractAccessError("");
    try {
      await DataServiceSQL.setStructurePractitionerAccess(practAccessStructure.id_structure, practAccessSelected);
      setPractAccessStructure(null);
    } catch (e) {
      setPractAccessError(intl.formatMessage({ id: 'message.error', defaultMessage: 'An error occurred' }));
    }
    setPractAccessLoading(false);
  };

  const openStructureUsers = async (structure) => {
    setSelectedStructure(structure);
    setStructureUsers([]);
    setUsersLoading(true);
    setShowUsers(true);
    setCredSendingUserId(null);
    setCredSentUserId(null);
    setCredErrorUserId(null);
    try {
      const response = await DataServiceSQL.getStructureUsers(structure.id_structure);
      setStructureUsers(response.data || []);
    } catch (e) {
      setStructureUsers([]);
    }
    setUsersLoading(false);
  };

  const closeStructureUsers = () => {
    setShowUsers(false);
    setSelectedStructure(null);
    setStructureUsers([]);
  };

  const openStructureAccount = async (s) => {
    setAccountStructure(s);
    setAccountExistingUser(null);
    setAccountFirstName("");
    setAccountLastName(s.structure_name || "");
    setAccountError("");
    setAccountSent(false);
    setAccountChecking(true);
    try {
      const response = await DataServiceSQL.getStructureUsers(s.id_structure);
      const email = (s.email || "").trim().toLowerCase();
      const existing = (response.data || []).find(u => u.email && u.email.trim().toLowerCase() === email);
      setAccountExistingUser(existing || null);
    } catch (e) {
      setAccountExistingUser(null);
    }
    setAccountChecking(false);
  };

  const sendStructureAccount = async () => {
    if (!accountStructure) return;
    if (!accountExistingUser && (!accountFirstName.trim() || !accountLastName.trim())) {
      setAccountError(intl.formatMessage({ id: 'structure.error.account', defaultMessage: 'Prénom, nom et email sont requis pour créer un compte.' }));
      return;
    }
    setAccountLoading(true);
    setAccountError("");
    try {
      const params = accountExistingUser
        ? { id_user: accountExistingUser.id_user, language: intl.locale || 'fr-FR' }
        : {
            first_name: accountFirstName.trim(),
            last_name: accountLastName.trim(),
            email: accountStructure.email.trim(),
            language: intl.locale || 'fr-FR',
          };
      await DataServiceSQL.sendStructureCredentials(accountStructure.id_structure, params);
      setAccountSent(true);
      if (!accountExistingUser) loadStructures();
    } catch (e) {
      setAccountError(intl.formatMessage({ id: 'message.error', defaultMessage: 'An error occurred' }));
    }
    setAccountLoading(false);
  };

  return (
    <div className={classes.popup_background} onClick={props.cancelPopup}>
      <div style={{ minHeight: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }} onClick={(e) => e.stopPropagation()}>
        <div className="management-menu-popup structure-management-popup" style={{ height: "auto", maxHeight: "92vh", overflowY: "auto", position: "relative" }}>

          {/* Close button top-right */}
          <button
            onClick={props.cancelPopup}
            style={{ position: 'absolute', top: 6, right: 20, zIndex: 10, background: '#fff', border: '1px solid #ccc', borderRadius: '50%', fontSize: 18, fontWeight: 'bold', lineHeight: 1, cursor: 'pointer', color: '#DB2828', width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }}
            title={intl.formatMessage({ id: 'button.close', defaultMessage: 'Fermer' })}
          >×</button>

          {/* Back button (only visible when in a sub-view) — placed to the left of the close button */}
          {(showUsers || showAddForm) && (
            <button
              onClick={() => { if (showUsers) { closeStructureUsers(); } else { setShowAddForm(false); } }}
              style={{ position: 'absolute', top: 6, right: 58, zIndex: 10, background: '#fff', border: '1px solid #ccc', borderRadius: '6px', fontSize: 13, fontWeight: 600, lineHeight: 1, cursor: 'pointer', color: '#2c3e50', height: 30, padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }}
              title={intl.formatMessage({ id: 'button.back', defaultMessage: 'Back' })}
            >
              <Icon name="arrow left" style={{ margin: 0 }} />
              <FormattedMessage id="button.back" defaultMessage="Back" />
            </button>
          )}

          {/* Title row */}
          <Row style={{ paddingBottom: "20px", alignItems: "center" }}>
            <Col style={{ fontWeight: "bold", fontSize: "2.2em", textAlign: "center" }}>
              <FormattedMessage id="structure.title" defaultMessage="Structures" />
            </Col>
          </Row>

          {showUsers ? (
            /* ---- Structure users panel ---- */
            <div>
              <div style={{ display: "flex", alignItems: "center", marginBottom: "18px", borderBottom: "2px solid #f0f0f0", paddingBottom: "12px" }}>
                <button className="btn btn-sm btn-outline-secondary" onClick={closeStructureUsers} style={{ marginRight: "12px", borderRadius: "6px" }}>
                  <Icon name="arrow left" /> <FormattedMessage id="button.back" defaultMessage="Back" />
                </button>
                <Icon name="users" size="large" color="blue" />
                <span style={{ marginLeft: "10px", fontWeight: 600, fontSize: "1.1em", color: "#2c3e50" }}>
                  {selectedStructure && selectedStructure.structure_name}
                </span>
              </div>
              {usersLoading ? (
                <center><p><FormattedMessage id="loading" defaultMessage="Loading..." /></p></center>
              ) : (
                <table className="table table-striped table-bordered" style={{ width: "100%" }}>
                  <thead>
                    <tr>
                      <th><FormattedMessage id="structure.users.lastname" defaultMessage="Last name" /></th>
                      <th><FormattedMessage id="structure.users.firstname" defaultMessage="First name" /></th>
                      <th><FormattedMessage id="structure.users.email" defaultMessage="Email" /></th>
                      <th><FormattedMessage id="structure.users.role" defaultMessage="Role" /></th>
                      <th><FormattedMessage id="structure.users.role_extra" defaultMessage="Role extra" /></th>
                      <th><FormattedMessage id="structure.users.profession" defaultMessage="Profession" /></th>
                      <th><FormattedMessage id="structure.users.created_at" defaultMessage="Created at" /></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {structureUsers.length === 0 ? (
                      <tr><td colSpan={8} style={{ textAlign: "center" }}><FormattedMessage id="table.nodata" defaultMessage="No data" /></td></tr>
                    ) : (
                      structureUsers.map((u, idx) => (
                        <tr key={u.id_user || idx}>
                          <td>{u.lastName || ""}</td>
                          <td>{u.firstName || ""}</td>
                          <td>{u.email || ""}</td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            <span style={{
                              display: "inline-block",
                              padding: "2px 8px",
                              borderRadius: "10px",
                              fontSize: "0.8em",
                              fontWeight: 600,
                              color: "#fff",
                              background: u.role === "admin" ? "#c0392b" : u.role === "staff" ? "#2980b9" : "#7f8c8d"
                            }}>
                              {u.role || ""}
                            </span>
                            {u.role === "admin" && u.role_extra === "admin_etc" && (
                              <span style={{
                                display: "inline-block",
                                marginLeft: "5px",
                                padding: "2px 8px",
                                borderRadius: "10px",
                                fontSize: "0.8em",
                                fontWeight: 600,
                                color: "#fff",
                                background: "#8e44ad"
                              }}>
                                Admin ETC
                              </span>
                            )}
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {u.role_extra ? (
                              <span style={{
                                display: "inline-block",
                                padding: "2px 8px",
                                borderRadius: "10px",
                                fontSize: "0.8em",
                                fontWeight: 600,
                                color: "#fff",
                                background: "#8e44ad"
                              }}>
                                {u.role_extra}
                              </span>
                            ) : (
                              <span style={{ color: "#bbb" }}>—</span>
                            )}
                          </td>
                          <td>{(u.profession && u.profession !== "null") ? u.profession : (u.category && u.category !== "null") ? u.category : ""}</td>
                          <td style={{ whiteSpace: "nowrap", fontSize: "0.85em" }}>
                            {u.created_at ? new Date(u.created_at).toLocaleDateString() : <span style={{ color: "#bbb" }}>—</span>}
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {credSentUserId === u.id_user ? (
                              <span style={{ color: "#27ae60", fontSize: "0.82em", fontWeight: 600 }}>
                                ✓ <FormattedMessage id="practitioner.credentialsSent" defaultMessage="Email envoyé" />
                              </span>
                            ) : credErrorUserId === u.id_user ? (
                              <span style={{ color: "#e74c3c", fontSize: "0.82em" }}>
                                ✗ <FormattedMessage id="message.error" defaultMessage="Erreur" />
                              </span>
                            ) : (
                              <button
                                className="btn btn-outline-secondary btn-sm"
                                style={{ fontSize: "0.78em", padding: "2px 8px" }}
                                disabled={credSendingUserId === u.id_user || !u.id_user}
                                onClick={() => handleSendUserCredentials(u)}
                              >
                                {credSendingUserId === u.id_user
                                  ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                  : <FormattedMessage id="practitioner.resendCredentials" defaultMessage="Renvoyer les identifiants" />
                                }
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>
          ) : showAddForm ? (
            /* ---- Add structure form ---- */
            <div style={{ display: "flex", justifyContent: "center", alignItems: "flex-start", padding: "4px 0" }}>
              <div style={{ width: "100%", maxWidth: "560px", background: "#fff", borderRadius: "12px", boxShadow: "0 4px 24px rgba(0,0,0,0.10)", padding: "28px 32px" }}>

                {/* Form header */}
                <div style={{ display: "flex", alignItems: "center", marginBottom: "22px", borderBottom: "2px solid #f0f0f0", paddingBottom: "14px" }}>
                  <Icon name="hospital" size="large" color="blue" />
                  <h5 style={{ margin: "0 0 0 10px", fontWeight: 600, color: "#2c3e50" }}>
                    <FormattedMessage id="structure.add" defaultMessage="Add structure" />
                  </h5>
                </div>

                {addError && <div className="alert alert-danger" style={{ borderRadius: "8px", fontSize: "0.9em" }}>{addError}</div>}

                {addSubmitted ? (
                  <div style={{ textAlign: "center", padding: "16px 0" }}>
                    <Icon name={addMessage.includes("err") || addMessage.includes("error") ? "times circle" : "check circle"} size="huge" color={addMessage.includes("err") || addMessage.includes("error") ? "red" : "green"} />
                    <p style={{ marginTop: "12px", fontSize: "1em", color: "#555" }}>{addMessage}</p>
                    <button className="btn btn-success" onClick={closeAddForm} style={{ marginTop: "8px", borderRadius: "6px" }}>
                      <FormattedMessage id="button.close" defaultMessage="Close" />
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Section: Identification */}
                    <div style={{ marginBottom: "18px" }}>
                      <p style={{ fontSize: "0.75em", fontWeight: 700, textTransform: "uppercase", color: "#888", letterSpacing: "0.08em", marginBottom: "10px" }}>
                        <FormattedMessage id="structure.section.identity" defaultMessage="Identification" />
                      </p>
                      <div style={{ marginBottom: "12px" }}>
                        <label style={{ fontWeight: 600, fontSize: "0.88em", color: "#444", marginBottom: "4px", display: "block" }}>
                          <FormattedMessage id="structure.name" defaultMessage="Structure name" /> <span style={{ color: "#e74c3c" }}>*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="structure_name"
                          value={addForm.structure_name}
                          onChange={handleAddFormInput}
                          style={{ borderRadius: "7px", borderColor: addError && !addForm.structure_name.trim() ? "#e74c3c" : undefined }}
                        />
                      </div>
                      <div>
                        <label style={{ fontWeight: 600, fontSize: "0.88em", color: "#444", marginBottom: "6px", display: "block" }}>
                          <FormattedMessage id="structure.type" defaultMessage="Type" />
                        </label>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                          {[
                            { value: "", label: "--" },
                            { value: "Hopital", label: "🏥 Hôpital" },
                            { value: "Clinique", label: "🏨 Clinique" },
                            { value: "Cabinet medical", label: "🩺 Cabinet médical" },
                            { value: "PSAD", label: "🚑 PSAD" }
                          ].map(opt => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setAddForm(prev => ({ ...prev, type: opt.value }))}
                              style={{
                                padding: "5px 14px",
                                borderRadius: "20px",
                                border: "1.5px solid",
                                borderColor: addForm.type === opt.value ? "#3498db" : "#ced4da",
                                background: addForm.type === opt.value ? "#ebf5fb" : "#fafafa",
                                color: addForm.type === opt.value ? "#2980b9" : "#555",
                                fontWeight: addForm.type === opt.value ? 600 : 400,
                                fontSize: "0.85em",
                                cursor: "pointer",
                                transition: "all 0.15s"
                              }}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Section: Contact */}
                    <div style={{ marginBottom: "18px" }}>
                      <p style={{ fontSize: "0.75em", fontWeight: 700, textTransform: "uppercase", color: "#888", letterSpacing: "0.08em", marginBottom: "10px" }}>
                        <FormattedMessage id="structure.section.contact" defaultMessage="Contact" />
                      </p>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                        <div>
                          <label style={{ fontWeight: 600, fontSize: "0.88em", color: "#444", marginBottom: "4px", display: "block" }}>
                            <Icon name="mail outline" style={{ marginRight: "4px" }} />
                            <FormattedMessage id="structure.email" defaultMessage="Email" />
                          </label>
                          <input type="email" className="form-control" name="email" value={addForm.email} onChange={handleAddFormInput} style={{ borderRadius: "7px" }} />
                        </div>
                        <div>
                          <label style={{ fontWeight: 600, fontSize: "0.88em", color: "#444", marginBottom: "4px", display: "block" }}>
                            <Icon name="phone" style={{ marginRight: "4px" }} />
                            <FormattedMessage id="structure.phone" defaultMessage="Phone" />
                          </label>
                          <input type="text" className="form-control" name="phone_number" value={addForm.phone_number} onChange={handleAddFormInput} style={{ borderRadius: "7px" }} />
                        </div>
                      </div>
                    </div>

                    {/* Section: Address */}
                    <div style={{ marginBottom: "18px" }}>
                      <p style={{ fontSize: "0.75em", fontWeight: 700, textTransform: "uppercase", color: "#888", letterSpacing: "0.08em", marginBottom: "10px" }}>
                        <FormattedMessage id="structure.section.location" defaultMessage="Localisation" />
                      </p>
                      <div>
                        <label style={{ fontWeight: 600, fontSize: "0.88em", color: "#444", marginBottom: "4px", display: "block" }}>
                          <Icon name="map marker alternate" style={{ marginRight: "4px" }} />
                          <FormattedMessage id="structure.address" defaultMessage="Address" />
                        </label>
                        <input type="text" className="form-control" name="address" value={addForm.address} onChange={handleAddFormInput} style={{ borderRadius: "7px" }} />
                      </div>
                    </div>

                    {/* Section: Schedule */}
                    <div style={{ marginBottom: "22px" }}>
                      <p style={{ fontSize: "0.75em", fontWeight: 700, textTransform: "uppercase", color: "#888", letterSpacing: "0.08em", marginBottom: "10px" }}>
                        <Icon name="clock outline" style={{ marginRight: "4px" }} />
                        <FormattedMessage id="structure.hours" defaultMessage="Schedule" />
                      </p>
                      <div style={{ background: "#f8f9fa", borderRadius: "8px", padding: "12px 16px" }}>
                        <div style={{ display: "grid", gridTemplateColumns: "80px 1fr 1fr", gap: "8px", alignItems: "center", marginBottom: "8px" }}>
                          <span style={{ fontSize: "0.8em", color: "#777", fontWeight: 600 }}></span>
                          <span style={{ fontSize: "0.8em", color: "#555", textAlign: "center", fontWeight: 600 }}>
                            <FormattedMessage id="account.structure.hours_schedule.begin" defaultMessage="Start" />
                          </span>
                          <span style={{ fontSize: "0.8em", color: "#555", textAlign: "center", fontWeight: 600 }}>
                            <FormattedMessage id="account.structure.hours_schedule.end" defaultMessage="End" />
                          </span>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "80px 1fr 1fr", gap: "8px", alignItems: "center", marginBottom: "6px" }}>
                          <span style={{ fontSize: "0.85em", fontWeight: 600, color: "#f39c12" }}>
                            ☀️ <FormattedMessage id="account.structure.hours_schedule.day" defaultMessage="Day" />
                          </span>
                          <input type="time" className="form-control form-control-sm" name="hours_day_start" value={addForm.hours_day_start} onChange={handleAddFormInput} style={{ borderRadius: "6px" }} />
                          <input type="time" className="form-control form-control-sm" name="hours_day_end" value={addForm.hours_day_end} onChange={handleAddFormInput} style={{ borderRadius: "6px" }} />
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "80px 1fr 1fr", gap: "8px", alignItems: "center" }}>
                          <span style={{ fontSize: "0.85em", fontWeight: 600, color: "#6c5ce7" }}>
                            🌙 <FormattedMessage id="account.structure.hours_schedule.night" defaultMessage="Night" />
                          </span>
                          <input type="time" className="form-control form-control-sm" name="hours_night_start" value={addForm.hours_night_start} onChange={handleAddFormInput} style={{ borderRadius: "6px" }} />
                          <input type="time" className="form-control form-control-sm" name="hours_night_end" value={addForm.hours_night_end} onChange={handleAddFormInput} style={{ borderRadius: "6px" }} />
                        </div>
                      </div>
                    </div>

                    {/* Section: Subscription (super admin only) */}
                    {isSuperAdmin && (
                      <div style={{ marginBottom: "18px" }}>
                        <p style={{ fontSize: "0.75em", fontWeight: 700, textTransform: "uppercase", color: "#888", letterSpacing: "0.08em", marginBottom: "10px" }}>
                          <Icon name="tag" style={{ marginRight: "4px" }} />
                          <FormattedMessage id="structure.subscription" defaultMessage="Subscription" />
                        </p>
                        <select
                          className="form-control"
                          name="FK_subscription"
                          value={addForm.FK_subscription}
                          onChange={handleAddFormInput}
                          style={{ borderRadius: "7px" }}
                        >
                          <option value="">{intl.formatMessage({ id: 'structure.subscription.select', defaultMessage: 'Select a subscription' })}</option>
                          {allSubscriptions.map(sub => (
                            <option key={sub.id_subscription} value={sub.id_subscription}>
                              {sub.id_subscription.slice(0, 8)}… — {sub.sub_used}/{sub.sub_total}
                              {` — ${getActiveFeatures(sub.features).length} feature(s)`}
                              {sub.expiration_date ? ` (exp. ${new Date(sub.expiration_date).toLocaleDateString()})` : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Section: Create account */}
                    <div style={{ marginBottom: "18px" }}>
                      <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", userSelect: "none" }}>
                        <input
                          type="checkbox"
                          checked={addCreateAccount}
                          onChange={e => setAddCreateAccount(e.target.checked)}
                          style={{ width: 16, height: 16, cursor: "pointer" }}
                        />
                        <span style={{ fontWeight: 600, fontSize: "0.88em", color: "#444" }}>
                          <FormattedMessage id="structure.createAccount" defaultMessage="Créer un compte et envoyer les identifiants" />
                        </span>
                      </label>
                      {addCreateAccount && (
                        <div style={{ marginTop: "10px", paddingLeft: "24px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                          <div>
                            <label style={{ fontWeight: 600, fontSize: "0.85em", color: "#555", marginBottom: "3px", display: "block" }}>
                              <FormattedMessage id="structure.adminFirstName" defaultMessage="Prénom" /> <span style={{ color: "#e74c3c" }}>*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              name="admin_first_name"
                              value={addForm.admin_first_name || ""}
                              onChange={handleAddFormInput}
                              style={{ borderRadius: "6px" }}
                            />
                          </div>
                          <div>
                            <label style={{ fontWeight: 600, fontSize: "0.85em", color: "#555", marginBottom: "3px", display: "block" }}>
                              <FormattedMessage id="structure.adminLastName" defaultMessage="Nom" /> <span style={{ color: "#e74c3c" }}>*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              name="admin_last_name"
                              value={addForm.admin_last_name || ""}
                              onChange={handleAddFormInput}
                              style={{ borderRadius: "6px" }}
                            />
                          </div>
                          <div style={{ gridColumn: "1 / -1", fontSize: "0.8em", color: "#888" }}>
                            <FormattedMessage id="structure.createAccount.hint" defaultMessage="L'email de la structure sera utilisé pour envoyer les identifiants." />
                          </div>
                        </div>
                      )}
                    </div>

                    {addMessage && <p style={{ textAlign: "center", color: "#888", fontSize: "0.9em" }}>{addMessage}</p>}

                    {/* Actions */}
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", borderTop: "1px solid #f0f0f0", paddingTop: "16px" }}>
                      <button className="btn btn-outline-secondary" onClick={() => setShowAddForm(false)} style={{ borderRadius: "7px", minWidth: "90px" }}>
                        <FormattedMessage id="button.cancel" defaultMessage="Cancel" />
                      </button>
                      <button className="btn btn-primary" onClick={saveStructure} disabled={addLoading} style={{ borderRadius: "7px", minWidth: "110px", fontWeight: 600 }}>
                        {addLoading
                          ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                          : <><Icon name="save outline" /><FormattedMessage id="button.save" defaultMessage="Save" /></>
                        }
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            /* ---- Structure list ---- */
            <>
              {/* Subscription filter badge */}
              {subscriptionFilter && (
                <Row style={{ marginBottom: "6px", alignItems: "center" }}>
                  <Col style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                    {props.onOpenSubscription && (
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => props.onOpenSubscription(subscriptionFilter)}
                        style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "0.85em", borderRadius: "6px", padding: "3px 10px" }}
                        title={intl.formatMessage({ id: 'structure.backToSubscription', defaultMessage: 'Retour aux abonnements' })}
                      >
                        <Icon name="arrow left" style={{ margin: 0 }} />
                        <FormattedMessage id="structure.backToSubscription" defaultMessage="Abonnements" />
                      </button>
                    )}
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#ebf5fb", border: "1px solid #aed6f1", borderRadius: "12px", padding: "3px 10px", fontSize: "0.85em", color: "#2980b9", fontWeight: 600 }}>
                      <Icon name="tag" style={{ margin: 0 }} />
                      <FormattedMessage id="structure.subscriptionFilter" defaultMessage="Abonnement" />
                      {" : "}
                      <span style={{ fontFamily: "monospace", color: "#1a5276" }}>{subscriptionFilter.slice(0, 8)}…</span>
                      <button
                        onClick={clearSubscriptionFilter}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#e74c3c", fontWeight: 700, lineHeight: 1, padding: "0 0 0 4px", fontSize: "1em" }}
                        title={intl.formatMessage({ id: 'structure.clearSubscriptionFilter', defaultMessage: 'Supprimer le filtre' })}
                      >×</button>
                    </span>
                  </Col>
                </Row>
              )}

              <div className="structure-list-toolbar">
                <div className="structure-filter-row">
                  <div className="structure-type-filters" role="group" aria-label={intl.formatMessage({ id: 'structure.type', defaultMessage: 'Type' })}>
                    {[
                      { label: intl.formatMessage({ id: 'structure.filter.all', defaultMessage: 'Toutes' }), value: "" },
                      { label: "H\u00f4pital", value: "Hopital" },
                      { label: "Clinique", value: "Clinique" },
                      { label: "Cabinet m\u00e9dical", value: "Cabinet medical" },
                      { label: "PSAD", value: "PSAD" }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        className={"btn btn-sm " + (typeFilter === opt.value ? "btn-primary" : "btn-outline-secondary")}
                        onClick={() => onTypeFilterChange(opt.value)}
                        aria-pressed={typeFilter === opt.value}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <div className="structure-results-summary">
                    {(() => {
                      const list = (searchQuery.length > 0 || typeFilter !== "" || subscriptionFilter) ? filteredStructures : allStructures;
                      const totalUsers = list.reduce((a, s) => a + (Number(s.nb_users) || 0), 0);
                      const totalPatients = list.reduce((a, s) => a + (Number(s.nb_patients) || 0), 0);
                      const totalGroups = list.reduce((a, s) => a + (Number(s.nb_groups) || 0), 0);
                      return <>
                        <span><strong>{list.length}</strong> <FormattedMessage id={(searchQuery.length > 0 || typeFilter !== "" || subscriptionFilter) ? "structure.results" : "structure.title"} defaultMessage={(searchQuery.length > 0 || typeFilter !== "" || subscriptionFilter) ? "r\u00e9sultats" : "Structures"} /></span>
                        <span className="structure-summary-divider">|</span>
                        <span><Icon name="users" /><strong>{totalUsers}</strong> <FormattedMessage id="structure.total_users" defaultMessage="users" /></span>
                        <span className="structure-summary-divider">|</span>
                        <span><Icon name="user" /><strong>{totalPatients}</strong> <FormattedMessage id="structure.total_patients" defaultMessage="patients" /></span>
                        <span className="structure-summary-divider">|</span>
                        <span><Icon name="object group outline" /><strong>{totalGroups}</strong> <FormattedMessage id="structure.total_groups" defaultMessage="groups" /></span>
                      </>;
                    })()}
                  </div>
                </div>

                <div className="structure-search-row">
                  <div className="structure-search-group">
                    <div className="structure-search-input">
                      <input
                        type="search"
                        className="form-control"
                        placeholder={intl.formatMessage({ id: 'structure.search', defaultMessage: 'Search...' })}
                        value={searchQuery}
                        onChange={onSearchChange}
                        onKeyDown={onSearchKeyDown}
                        aria-label={intl.formatMessage({ id: 'structure.search', defaultMessage: 'Search...' })}
                      />
                      {searchQuery.length > 0 && (
                        <button
                          type="button"
                          className="structure-search-clear"
                          onClick={clearSearch}
                          aria-label={intl.formatMessage({ id: 'button.clear', defaultMessage: 'Clear' })}
                        >
                          &times;
                        </button>
                      )}
                    </div>
                    <button
                      className="btn btn-outline-secondary structure-search-button"
                      onClick={applySearch}
                      aria-label={intl.formatMessage({ id: 'structure.search', defaultMessage: 'Search...' })}
                    >
                      <Icon name="search" style={{ margin: 0 }} />
                    </button>
                  </div>
                  <div className="structure-list-actions">
                    <label className="structure-page-size">
                      <FormattedMessage id="structure.pagination" defaultMessage="Pagination" />
                      <select className="form-control" value={pageSize} onChange={onPageSizeChange}>
                        {PAGE_SIZE_OPTIONS.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </label>
                    <button className="btn btn-sm btn-success" onClick={openAddForm}>
                      <Icon name="plus" style={{ margin: "0 5px 0 0" }} />
                      <FormattedMessage id="structure.add" defaultMessage="Add structure" />
                    </button>
                  </div>
                </div>

                <div className="structure-pagination-controls" aria-label={intl.formatMessage({ id: 'structure.pagination', defaultMessage: 'Pagination' })}>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} aria-label={intl.formatMessage({ id: 'eq5d.form.arrow.previous', defaultMessage: 'Previous' })}>&laquo;</button>
                  <span className="structure-pagination-label">{currentPage} / {totalPages}</span>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} aria-label={intl.formatMessage({ id: 'eq5d.form.arrow.next', defaultMessage: 'Next' })}>&raquo;</button>
                </div>
              </div>

              {/* Responsive structure cards */}
              <div className="structure-card-grid" aria-live="polite">
                {loading ? (
                  <div className="structure-list-state"><FormattedMessage id="loading" defaultMessage="Loading..." /></div>
                ) : paginatedStructures.length === 0 ? (
                  <div className="structure-list-state"><FormattedMessage id="table.nodata" defaultMessage="No data" /></div>
                ) : (
                  paginatedStructures.map((s, idx) => {
                    const activeFeatures = getActiveFeatures(s.features);
                    const featureTooltipId = `features-card-tt-${s.id_structure || idx}`;
                    const isPsad = s.type && s.type.toLowerCase() === 'psad';

                    return (
                      <article className="structure-card" key={s.id_structure || idx}>
                        <header className="structure-card-header">
                          <div className="structure-card-heading">
                            <h3 className="structure-card-name">{s.structure_name}</h3>
                            <div className="structure-card-id">
                              <span>{s.id_structure ? s.id_structure.slice(0, 8) : ""}</span>
                              {s.id_structure && (
                                <button
                                  className="btn btn-sm"
                                  title={intl.formatMessage({ id: 'structure.copyId', defaultMessage: 'Copier l\'ID' })}
                                  aria-label={intl.formatMessage({ id: 'structure.copyId', defaultMessage: 'Copier l\'ID' })}
                                  onClick={() => {
                                    navigator.clipboard.writeText(s.id_structure).then(() => {
                                      setCopiedId(s.id_structure);
                                      setTimeout(() => setCopiedId(prev => prev === s.id_structure ? null : prev), 1500);
                                    });
                                  }}
                                  style={{ padding: "0 4px", lineHeight: 1, border: "none", background: "transparent", color: copiedId === s.id_structure ? "#27ae60" : "#89939e" }}
                                >
                                  <Icon name={copiedId === s.id_structure ? "check" : "copy outline"} style={{ margin: 0 }} />
                                </button>
                              )}
                            </div>
                          </div>
                          <span className="structure-card-type">{s.type || <FormattedMessage id="structure.type.unknown" defaultMessage="Non renseigné" />}</span>
                        </header>

                        <div className="structure-card-body">
                          <section>
                            <div className="structure-card-section-title"><FormattedMessage id="structure.section.contact" defaultMessage="Contact" /></div>
                            <div className="structure-contact-grid">
                              <div className="structure-contact-item">
                                <Icon name="mail outline" />
                                <span className={s.email ? "structure-contact-value" : "structure-contact-empty"}>{s.email || "—"}</span>
                              </div>
                              <div className="structure-contact-item">
                                <Icon name="phone" />
                                <span className={s.phone_number ? "structure-contact-value" : "structure-contact-empty"}>{s.phone_number || "—"}</span>
                              </div>
                              <div className="structure-contact-item" style={{ gridColumn: "1 / -1" }}>
                                <Icon name="map marker alternate" />
                                <span className={s.address ? "structure-contact-value" : "structure-contact-empty"}>{s.address || "—"}</span>
                              </div>
                            </div>
                          </section>

                          <div className="structure-card-meta-grid">
                            <section className="structure-card-meta">
                              <div className="structure-card-section-title"><FormattedMessage id="structure.subscription" defaultMessage="Subscription" /></div>
                              {s.sub_total != null ? (
                                <>
                                  <div className="structure-subscription-usage">
                                    <span style={{ fontWeight: 700, color: s.sub_used >= s.sub_total ? "#c0392b" : "#178a50" }}>{s.sub_used} / {s.sub_total}</span>
                                    {s.FK_subscription && props.onOpenSubscription && (
                                      <button
                                        className="btn btn-sm"
                                        style={{ padding: "0 3px", border: "none", background: "transparent", color: "#2980b9", lineHeight: 1 }}
                                        title={intl.formatMessage({ id: 'structure.openSubscription', defaultMessage: 'Voir cet abonnement' })}
                                        aria-label={intl.formatMessage({ id: 'structure.openSubscription', defaultMessage: 'Voir cet abonnement' })}
                                        onClick={() => props.onOpenSubscription(s.FK_subscription)}
                                      >
                                        <Icon name="external alternate" style={{ margin: 0 }} />
                                      </button>
                                    )}
                                  </div>
                                  {s.expiration_date && <div className="structure-subscription-details">{new Date(s.expiration_date).toLocaleDateString()}</div>}
                                  {s.FK_subscription && (
                                    <div className="structure-card-id">
                                      <span>{s.FK_subscription.slice(0, 8)}…</span>
                                      <button
                                        className="btn btn-sm"
                                        style={{ padding: "0 3px", border: "none", background: "transparent", color: copiedSubId === s.FK_subscription ? "#27ae60" : "#89939e", lineHeight: 1 }}
                                        title={intl.formatMessage({ id: 'structure.copySubscriptionId', defaultMessage: 'Copier l\'ID abonnement' })}
                                        aria-label={intl.formatMessage({ id: 'structure.copySubscriptionId', defaultMessage: 'Copier l\'ID abonnement' })}
                                        onClick={() => {
                                          navigator.clipboard.writeText(s.FK_subscription).then(() => {
                                            setCopiedSubId(s.FK_subscription);
                                            setTimeout(() => setCopiedSubId(prev => prev === s.FK_subscription ? null : prev), 1500);
                                          });
                                        }}
                                      >
                                        <Icon name={copiedSubId === s.FK_subscription ? "check" : "copy outline"} style={{ margin: 0 }} />
                                      </button>
                                    </div>
                                  )}
                                </>
                              ) : <span className="structure-contact-empty">—</span>}
                            </section>

                            <section className="structure-card-meta">
                              <div className="structure-card-section-title"><FormattedMessage id="structure.features" defaultMessage="Features" /></div>
                              {activeFeatures.length > 0 ? (
                                <>
                                  <Tooltip id={featureTooltipId} style={{ zIndex: 9999, maxWidth: "320px" }}>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", padding: "2px 0" }}>
                                      {activeFeatures.map(feature => <span key={feature} style={{ background: "#1a5276", borderRadius: "8px", padding: "1px 8px", color: "#d6eaf8" }}>{feature}</span>)}
                                    </div>
                                  </Tooltip>
                                  <span
                                    data-tooltip-id={featureTooltipId}
                                    data-tooltip-place="left"
                                    style={{ cursor: "default", background: "#ebf5fb", border: "1px solid #aed6f1", borderRadius: "10px", padding: "2px 10px", color: "#2980b9", fontWeight: 600, display: "inline-block", fontSize: "0.82rem" }}
                                  >
                                    {activeFeatures.length} feature{activeFeatures.length > 1 ? "s" : ""}
                                  </span>
                                </>
                              ) : <span className="structure-contact-empty">—</span>}
                            </section>
                          </div>

                          <section>
                            <div className="structure-card-section-title"><FormattedMessage id="structure.activity" defaultMessage="Activité" /></div>
                            <div className="structure-metrics">
                              <div className="structure-metric-group">
                                <div className="structure-metric-label"><FormattedMessage id="structure.patients_groups" defaultMessage="Patients / Groups" /></div>
                                <div className="structure-metric-values">
                                  <span style={{ color: "#8e44ad" }} title={intl.formatMessage({ id: 'structure.total_patients', defaultMessage: 'patients' })}><Icon name="user" />{s.nb_patients ?? 0}</span>
                                  <span style={{ color: "#168577" }} title={intl.formatMessage({ id: 'structure.total_groups', defaultMessage: 'groups' })}><Icon name="object group outline" />{s.nb_groups ?? 0}</span>
                                </div>
                              </div>
                              <div className="structure-metric-group">
                                <div className="structure-metric-label"><FormattedMessage id="structure.devices_gateways" defaultMessage="Devices / Gateways" /></div>
                                <div className="structure-metric-values">
                                  <span style={{ color: "#c96913" }}><Icon name="microchip" />{s.nb_devices ?? 0}</span>
                                  <span style={{ color: "#2c3e50" }}><Icon name="wifi" />{s.nb_gateways ?? 0}</span>
                                </div>
                              </div>
                              <div className="structure-metric-group">
                                <div className="structure-metric-label"><FormattedMessage id="structure.practitioners_psad" defaultMessage="Practitioners / PSAD" /></div>
                                <div className="structure-metric-values">
                                  <span style={{ color: "#1a5276" }} title={(s.nb_practitioners ?? 0) > 0 ? `${s.nb_practitioners_fk_user ?? 0}+${s.nb_practitioners_linked ?? 0}` : undefined}><Icon name="doctor" />{s.nb_practitioners ?? 0}</span>
                                  <span style={{ color: "#6c3483" }}><Icon name="hospital outline" />{s.nb_psad ?? 0}</span>
                                </div>
                              </div>
                            </div>
                          </section>
                        </div>

                        <footer className="structure-card-actions">
                          <button className="btn btn-sm btn-outline-primary" title={intl.formatMessage({ id: 'structure.users.btn', defaultMessage: 'Users' })} onClick={() => openStructureUsers(s)}>
                            <Icon name="users" style={{ margin: 0 }} />
                            <span className="structure-card-action-label"><FormattedMessage id="structure.users.btn" defaultMessage="Users" />{s.nb_users > 0 ? ` (${s.nb_users})` : ""}</span>
                          </button>
                          {isAdmin && (
                            <>
                              {isSuperAdmin && (
                                <button className="btn btn-sm btn-outline-primary" title={intl.formatMessage({ id: s.email ? 'structure.account.btn' : 'structure.account.noEmail', defaultMessage: s.email ? 'Compte de la structure' : 'Aucun email enregistré pour cette structure' })} onClick={() => openStructureAccount(s)} disabled={!s.email}>
                                  <Icon name="key" style={{ margin: 0 }} />
                                  <span className="structure-card-action-label"><FormattedMessage id="structure.account.btn" defaultMessage="Compte" /></span>
                                </button>
                              )}
                              {isSuperAdmin && (
                                <button className="btn btn-sm btn-outline-success" title={intl.formatMessage({ id: 'structure.addUser.btn', defaultMessage: 'Add user' })} onClick={() => { setAddPersonStructure(s); setAddPersonRole('user'); }}>
                                  <Icon name="user plus" style={{ margin: 0 }} />
                                  <span className="structure-card-action-label"><FormattedMessage id="structure.addUser.btn" defaultMessage="Add user" /></span>
                                </button>
                              )}
                              {isSuperAdmin && (
                                <button className="btn btn-sm btn-outline-success" title={intl.formatMessage({ id: 'structure.addPatient.btn', defaultMessage: 'Add patient' })} onClick={() => setAddPatientStructure(s)}>
                                  <Icon name="bed" style={{ margin: 0 }} />
                                  <span className="structure-card-action-label"><FormattedMessage id="structure.addPatient.btn" defaultMessage="Add patient" /></span>
                                </button>
                              )}
                              <button className="btn btn-sm btn-outline-info" title={intl.formatMessage({ id: 'structure.addStaff.btn', defaultMessage: 'Add a caregiver' })} onClick={() => { setAddPersonStructure(s); setAddPersonRole('staff'); }}>
                                <Icon name="user md" style={{ margin: 0 }} />
                                <span className="structure-card-action-label"><FormattedMessage id="structure.addStaff.btn" defaultMessage="Add a caregiver" /></span>
                              </button>
                              {isSuperAdmin && (
                                <button className="btn btn-sm btn-outline-warning" title={intl.formatMessage({ id: 'structure.assignSubscription.btn', defaultMessage: 'Assign subscription' })} onClick={() => openAssignSub(s)}>
                                  <Icon name="tag" style={{ margin: 0 }} />
                                  <span className="structure-card-action-label"><FormattedMessage id="structure.assignSubscription.btn" defaultMessage="Subscription" /></span>
                                </button>
                              )}
                              {isSuperAdmin && !isPsad && (
                                <button className="btn btn-sm btn-outline-dark" title={intl.formatMessage({ id: 'structure.managePsad.btn', defaultMessage: 'PSAD disponibles' })} onClick={() => openPsadAccess(s)}>
                                  <Icon name="hospital outline" style={{ margin: 0 }} />
                                  <span className="structure-card-action-label"><FormattedMessage id="structure.managePsad.btn" defaultMessage="PSAD disponibles" /></span>
                                </button>
                              )}
                              {isSuperAdmin && (
                                <button className="btn btn-sm btn-outline-dark" title={intl.formatMessage({ id: 'structure.managePractitioners.btn', defaultMessage: 'Praticiens disponibles' })} onClick={() => openPractitionerAccess(s)}>
                                  <Icon name="user md" style={{ margin: 0 }} />
                                  <span className="structure-card-action-label"><FormattedMessage id="structure.managePractitioners.btn" defaultMessage="Praticiens disponibles" /></span>
                                </button>
                              )}
                            </>
                          )}
                        </footer>
                      </article>
                    );
                  })
                )}
              </div>

              {/* Pagination controls */}
              <div className="structure-pagination-controls" style={{ padding: "14px 0 4px" }} aria-label={intl.formatMessage({ id: 'structure.pagination', defaultMessage: 'Pagination' })}>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} aria-label={intl.formatMessage({ id: 'eq5d.form.arrow.previous', defaultMessage: 'Previous' })}>&laquo;</button>
                <span className="structure-pagination-label">{currentPage} / {totalPages}</span>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} aria-label={intl.formatMessage({ id: 'eq5d.form.arrow.next', defaultMessage: 'Next' })}>&raquo;</button>
              </div>
            </>
          )}

        </div>
      </div>
      {addPersonStructure && addPersonRole && (
        <div onClick={(e) => e.stopPropagation()}>
          {addPersonRole === 'user' ? (
            <AddUser
              cancelPopup={() => { setAddPersonStructure(null); setAddPersonRole(null); }}
              closeAfterAdd={() => { setAddPersonStructure(null); setAddPersonRole(null); loadStructures(); }}
              targetStructureId={addPersonStructure.id_structure}
              accountInformation={props.accountInformation}
            />
          ) : (
            <AddStaff
              cancelPopup={() => { setAddPersonStructure(null); setAddPersonRole(null); }}
              closeAfterAdd={() => { setAddPersonStructure(null); setAddPersonRole(null); loadStructures(); }}
              structure_Id={addPersonStructure.id_structure}
              targetStructureId={addPersonStructure.id_structure}
              role={addPersonRole}
              accountInformation={props.accountInformation}
            />
          )}
        </div>
      )}
      {addPatientStructure && (
        <div onClick={(e) => e.stopPropagation()}>
          <AddStructurePatient
            structure={addPatientStructure}
            cancelPopup={() => setAddPatientStructure(null)}
            closeAfterAdd={() => { setAddPatientStructure(null); loadStructures(); }}
            accountInformation={props.accountInformation}
          />
        </div>
      )}
      {/* Assign subscription modal */}
      {assignSubStructure && (
        <div
          onClick={() => setAssignSubStructure(null)}
          style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: "12px", padding: "28px 32px", minWidth: "380px", maxWidth: "480px", boxShadow: "0 4px 24px rgba(0,0,0,0.18)" }}
          >
            <h5 style={{ marginBottom: "16px", fontWeight: 600, color: "#2c3e50" }}>
              <Icon name="tag" color="orange" />
              <FormattedMessage id="structure.assignSubscription" defaultMessage="Assign subscription" />
            </h5>
            <p style={{ fontSize: "0.9em", color: "#555", marginBottom: "14px" }}>
              <strong>{assignSubStructure.structure_name}</strong>
            </p>
            {assignSubError && <div className="alert alert-danger" style={{ fontSize: "0.88em" }}>{assignSubError}</div>}
            <select
              className="form-control"
              value={assignSubSelected}
              onChange={(e) => setAssignSubSelected(e.target.value)}
              style={{ borderRadius: "7px", marginBottom: "18px" }}
            >
              <option value="">{intl.formatMessage({ id: 'structure.subscription.select', defaultMessage: 'Select a subscription' })}</option>
              {allSubscriptions.map(sub => (
                <option key={sub.id_subscription} value={sub.id_subscription}>
                  {sub.id_subscription.slice(0, 8)}… — {sub.sub_used}/{sub.sub_total}
                  {` — ${getActiveFeatures(sub.features).length} feature(s)`}
                  {sub.expiration_date ? ` (exp. ${new Date(sub.expiration_date).toLocaleDateString()})` : ""}
                </option>
              ))}
            </select>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button className="btn btn-outline-secondary" onClick={() => setAssignSubStructure(null)} style={{ borderRadius: "7px" }}>
                <FormattedMessage id="button.cancel" defaultMessage="Cancel" />
              </button>
              <button className="btn btn-warning" onClick={saveAssignSubscription} disabled={assignSubLoading} style={{ borderRadius: "7px", fontWeight: 600 }}>
                {assignSubLoading
                  ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  : <><Icon name="save outline" /><FormattedMessage id="button.save" defaultMessage="Save" /></>
                }
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Manage available PSAD structures modal */}
      {psadAccessStructure && (
        <div
          onClick={() => setPsadAccessStructure(null)}
          style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: "12px", padding: "28px 32px", minWidth: "400px", maxWidth: "520px", boxShadow: "0 4px 24px rgba(0,0,0,0.18)" }}
          >
            <h5 style={{ marginBottom: "12px", fontWeight: 600, color: "#2c3e50" }}>
              <Icon name="hospital outline" color="black" />
              <FormattedMessage id="structure.managePsad" defaultMessage="PSAD disponibles pour la structure" />
            </h5>
            <p style={{ fontSize: "0.9em", color: "#555", marginBottom: "10px" }}>
              <strong>{psadAccessStructure.structure_name}</strong>
            </p>
            <p style={{ fontSize: "0.82em", color: "#888", marginBottom: "12px" }}>
              <FormattedMessage id="structure.managePsad.help" defaultMessage="Cochez les PSAD que cette structure pourra utiliser. Si aucun n'est coché, tous les PSAD restent disponibles." />
            </p>
            {psadAccessError && <div className="alert alert-danger" style={{ fontSize: "0.88em" }}>{psadAccessError}</div>}
            <div style={{ maxHeight: "320px", overflowY: "auto", border: "1px solid #eee", borderRadius: "8px", padding: "8px 12px", marginBottom: "16px" }}>
              {allPsadStructures.length === 0 ? (
                <div style={{ color: "#bbb", textAlign: "center", padding: "10px 0" }}>
                  <FormattedMessage id="structure.psad.none" defaultMessage="Aucune structure PSAD" />
                </div>
              ) : (
                allPsadStructures.map(p => (
                  <label key={p.id_structure} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "5px 0", cursor: "pointer", fontWeight: "normal" }}>
                    <input
                      type="checkbox"
                      checked={psadAccessSelected.includes(p.id_structure)}
                      onChange={() => togglePsadAccess(p.id_structure)}
                    />
                    <span>{p.structure_name}{p.email ? <span style={{ color: "#888", fontSize: "0.85em" }}> — {p.email}</span> : null}</span>
                  </label>
                ))
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button className="btn btn-outline-secondary" onClick={() => setPsadAccessStructure(null)} style={{ borderRadius: "7px" }}>
                <FormattedMessage id="button.cancel" defaultMessage="Cancel" />
              </button>
              <button className="btn btn-primary" onClick={savePsadAccess} disabled={psadAccessLoading} style={{ borderRadius: "7px", fontWeight: 600 }}>
                {psadAccessLoading
                  ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  : <><Icon name="save outline" /><FormattedMessage id="button.save" defaultMessage="Save" /></>
                }
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Manage available practitioners modal */}
      {practAccessStructure && (
        <div
          onClick={() => setPractAccessStructure(null)}
          style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: "12px", padding: "28px 32px", minWidth: "400px", maxWidth: "520px", boxShadow: "0 4px 24px rgba(0,0,0,0.18)" }}
          >
            <h5 style={{ marginBottom: "12px", fontWeight: 600, color: "#2c3e50" }}>
              <Icon name="user md" color="black" />
              <FormattedMessage id="structure.managePractitioners" defaultMessage="Praticiens disponibles pour la structure" />
            </h5>
            <p style={{ fontSize: "0.9em", color: "#555", marginBottom: "10px" }}>
              <strong>{practAccessStructure.structure_name}</strong>
            </p>
            <p style={{ fontSize: "0.82em", color: "#888", marginBottom: "12px" }}>
              <FormattedMessage id="structure.managePractitioners.help" defaultMessage="Cochez les praticiens disponibles pour cette structure. Si aucun n'est coché, tous restent disponibles." />
            </p>
            {practAccessError && <div className="alert alert-danger" style={{ fontSize: "0.88em" }}>{practAccessError}</div>}
            <div style={{ maxHeight: "320px", overflowY: "auto", border: "1px solid #eee", borderRadius: "8px", padding: "8px 12px", marginBottom: "16px" }}>
              {allPractitioners.length === 0 ? (
                <div style={{ color: "#bbb", textAlign: "center", padding: "10px 0" }}>
                  <FormattedMessage id="structure.practitioners.none" defaultMessage="Aucun praticien" />
                </div>
              ) : (
                allPractitioners.map(p => {
                  const name = [p.family, Array.isArray(p.given) ? p.given.join(' ') : p.given].filter(Boolean).join(' ');
                  const sub = p.profession || p.mssante_email || p.email || null;
                  return (
                    <label key={p.id_practitioner} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "5px 0", cursor: "pointer", fontWeight: "normal" }}>
                      <input
                        type="checkbox"
                        checked={practAccessSelected.includes(p.id_practitioner)}
                        onChange={() => togglePractitionerAccess(p.id_practitioner)}
                      />
                      <span>{name || p.rpps || p.id_practitioner}{sub ? <span style={{ color: "#888", fontSize: "0.85em" }}> — {sub}</span> : null}</span>
                    </label>
                  );
                })
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button className="btn btn-outline-secondary" onClick={() => setPractAccessStructure(null)} style={{ borderRadius: "7px" }}>
                <FormattedMessage id="button.cancel" defaultMessage="Cancel" />
              </button>
              <button className="btn btn-primary" onClick={savePractitionerAccess} disabled={practAccessLoading} style={{ borderRadius: "7px", fontWeight: 600 }}>
                {practAccessLoading
                  ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  : <><Icon name="save outline" /><FormattedMessage id="button.save" defaultMessage="Save" /></>
                }
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Structure account modal: resend or create the account tied to the structure email */}
      {accountStructure && (
        <div
          onClick={() => setAccountStructure(null)}
          style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: "12px", padding: "28px 32px", minWidth: "400px", maxWidth: "500px", boxShadow: "0 4px 24px rgba(0,0,0,0.18)" }}
          >
            <h5 style={{ marginBottom: "12px", fontWeight: 600, color: "#2c3e50" }}>
              <Icon name="key" color="blue" />
              <FormattedMessage id="structure.account.title" defaultMessage="Compte de la structure" />
            </h5>
            <p style={{ fontSize: "0.9em", color: "#555", marginBottom: "14px" }}>
              <strong>{accountStructure.structure_name}</strong>
              <span style={{ color: "#888" }}> — {accountStructure.email}</span>
            </p>
            {accountError && <div className="alert alert-danger" style={{ fontSize: "0.88em" }}>{accountError}</div>}
            {accountChecking ? (
              <center><p><FormattedMessage id="loading" defaultMessage="Loading..." /></p></center>
            ) : accountSent ? (
              <div style={{ textAlign: "center", padding: "10px 0" }}>
                <Icon name="check circle" size="big" color="green" />
                <p style={{ marginTop: "10px", fontSize: "0.92em", color: "#555" }}>
                  <FormattedMessage id="structure.account.sent" defaultMessage="Identifiants envoyés à {email}" values={{ email: accountStructure.email }} />
                </p>
                <button className="btn btn-success" onClick={() => setAccountStructure(null)} style={{ marginTop: "6px", borderRadius: "7px" }}>
                  <FormattedMessage id="button.close" defaultMessage="Close" />
                </button>
              </div>
            ) : (
              <>
                {accountExistingUser ? (
                  <p style={{ fontSize: "0.88em", color: "#555", marginBottom: "16px" }}>
                    <Icon name="info circle" color="blue" />
                    <FormattedMessage id="structure.account.exists" defaultMessage="Un compte existe déjà avec l'email de la structure ({name}). Vous pouvez renvoyer les identifiants." values={{ name: `${accountExistingUser.firstName || ""} ${accountExistingUser.lastName || ""}`.trim() }} />
                  </p>
                ) : (
                  <>
                    <p style={{ fontSize: "0.88em", color: "#555", marginBottom: "12px" }}>
                      <Icon name="info circle" color="orange" />
                      <FormattedMessage id="structure.account.missing" defaultMessage="Aucun compte n'existe avec l'email de la structure. Renseignez le prénom et le nom pour le créer et envoyer les identifiants." />
                    </p>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "16px" }}>
                      <div>
                        <label style={{ fontWeight: 600, fontSize: "0.85em", color: "#555", marginBottom: "3px", display: "block" }}>
                          <FormattedMessage id="structure.adminFirstName" defaultMessage="Prénom" /> <span style={{ color: "#e74c3c" }}>*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={accountFirstName}
                          onChange={(e) => setAccountFirstName(e.target.value)}
                          style={{ borderRadius: "6px" }}
                        />
                      </div>
                      <div>
                        <label style={{ fontWeight: 600, fontSize: "0.85em", color: "#555", marginBottom: "3px", display: "block" }}>
                          <FormattedMessage id="structure.adminLastName" defaultMessage="Nom" /> <span style={{ color: "#e74c3c" }}>*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={accountLastName}
                          onChange={(e) => setAccountLastName(e.target.value)}
                          style={{ borderRadius: "6px" }}
                        />
                      </div>
                    </div>
                  </>
                )}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                  <button className="btn btn-outline-secondary" onClick={() => setAccountStructure(null)} style={{ borderRadius: "7px" }}>
                    <FormattedMessage id="button.cancel" defaultMessage="Cancel" />
                  </button>
                  <button className="btn btn-primary" onClick={sendStructureAccount} disabled={accountLoading} style={{ borderRadius: "7px", fontWeight: 600 }}>
                    {accountLoading
                      ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      : accountExistingUser
                        ? <><Icon name="send" /><FormattedMessage id="structure.account.resend" defaultMessage="Renvoyer les identifiants" /></>
                        : <><Icon name="send" /><FormattedMessage id="structure.account.create" defaultMessage="Créer le compte et envoyer les identifiants" /></>
                    }
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default withStyles(Constants.styles)(StructureManagementMenu);
